import { useEffect, useState } from 'react';
import { downloadDatasetExport, fetchGazeResults } from '../api';
import type { GazeTestResult } from '../types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Stack,
  Chip,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import PageHeader from '../components/PageHeader';
import { formatScreeningLabel } from '../utils/screeningLabel';
import { productTokens } from '../theme';

const ResearchEyeIcon = () => (
  <Box sx={{ color: 'secondary.main', mb: 1 }}>
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 5V3M12 21v-2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

export default function Dashboard() {
  const [results, setResults] = useState<GazeTestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'json' | 'csv' | null>(null);
  const navigate = useNavigate();
  const { isAuthed } = useAuthStore();

  const handleExport = async (format: 'json' | 'csv') => {
    setExporting(format);
    setError(null);
    try {
      await downloadDatasetExport(format);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '导出失败');
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    let cancel = false;
    (async () => {
      try {
        const data = await fetchGazeResults();
        if (!cancel) {
          setResults(
            [...data].sort(
              (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
            ),
          );
        }
      } catch (e: unknown) {
        if (!cancel) setError(e instanceof Error ? e.message : '加载失败');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isAuthed]);

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: '100%',
        minHeight: 0,
        py: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader
        title="交互评估控制台"
        subtitle="查看交互式眼动评估记录，启动新的采集流程"
        action={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => void handleExport('json')}
              disabled={exporting !== null}
              sx={{ px: 3, py: 1.5, fontWeight: 700 }}
            >
              {exporting === 'json' ? '导出中…' : '导出 JSON 清单'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => void handleExport('csv')}
              disabled={exporting !== null}
              sx={{ px: 3, py: 1.5, fontWeight: 700 }}
            >
              {exporting === 'csv' ? '导出中…' : '导出 CSV 清单'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate('/test')}
              sx={{ px: 4, py: 1.5, fontWeight: 700 }}
            >
              开始新评估
            </Button>
          </Stack>
        }
      />

      <Card variant="outlined" sx={{ mb: 2, overflow: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
        <CardContent
          sx={{
            p: 0,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: 0 },
          }}
        >
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(productTokens.iris, 0.035),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h6" fontWeight={750} color="primary.dark">
                  历史评估记录
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  所有已提交的受试者数据与系统初步筛查分析
                </Typography>
              </Box>
              {results && results.length > 0 ? (
                <Chip
                  label={`总计 ${results.length} 条记录`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700, bgcolor: 'background.paper' }}
                />
              ) : null}
            </Stack>
          </Box>

          {error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            </Box>
          ) : null}

          <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, minHeight: 0, overflow: 'auto' }}>
            {results === null ? (
              <Stack spacing={2}>
                <Skeleton variant="rounded" height={80} />
                <Skeleton variant="rounded" height={80} />
                <Skeleton variant="rounded" height={80} />
              </Stack>
            ) : results.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  borderStyle: 'dashed',
                  borderRadius: 1,
                  p: { xs: 3, sm: 5 },
                  textAlign: 'center',
                  bgcolor: alpha(productTokens.iris, 0.025),
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <ResearchEyeIcon />
                  <Typography variant="h6" fontWeight={800}>
                    暂无待处理评估记录
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 400, mb: 2.5 }}
                  >
                    系统尚未检测到任何已完成的测试报告。请通过上方“开始新评估”按钮启动约 90
                    秒的交互式眼动筛查流程。
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate('/test')}
                    sx={{ px: 4 }}
                  >
                    立即启动筛查采集
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <List disablePadding>
                {results.map((r, idx) => {
                  const isScored = String(r.status || '').toLowerCase() === 'scored';
                  const hasScore =
                    isScored &&
                    typeof r.confidence === 'number' &&
                    Number.isFinite(r.confidence) &&
                    (r.label || '').trim().length > 0;
                  const confidencePct =
                    typeof r.confidence === 'number'
                      ? Math.round(Math.max(0, Math.min(1, r.confidence)) * 100)
                      : null;
                  const display = hasScore
                    ? formatScreeningLabel(r.label)
                    : { text: isScored ? '评分中' : '采集完成', color: 'default' as const };

                  return (
                    <Box key={r.id}>
                      {idx > 0 && <Divider sx={{ my: 1, opacity: 0.6 }} />}
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => navigate(`/results/${r.id}`)}
                          sx={{
                            borderRadius: 1,
                            py: 2,
                            px: 2,
                            border: '1px solid transparent',
                            transition: 'background-color 160ms ease, border-color 160ms ease',
                            '&:hover': {
                              bgcolor: alpha(productTokens.iris, 0.045),
                              borderColor: alpha(productTokens.iris, 0.12),
                            },
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                            sx={{ width: '100%' }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800, color: 'text.primary' }}
                              >
                                {r.name
                                  ? r.name
                                  : r.childClinicId
                                    ? `儿童 ${r.childClinicId}`
                                    : '匿名受试者'}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.25 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 6v6l4 2" />
                                  </svg>
                                  {new Date(r.timestamp).toLocaleString('zh-CN', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </Typography>
                                <Typography variant="caption" color="divider">
                                  |
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  样本 ID: {String(r.id).padStart(8, '0')}
                                </Typography>
                                {r.childClinicId ? (
                                  <>
                                    <Typography variant="caption" color="divider">
                                      |
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      儿童编号: {r.childClinicId}
                                    </Typography>
                                  </>
                                ) : null}
                              </Stack>
                            </Box>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                              sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
                            >
                              {r.hasVideo ? (
                                <Chip
                                  label="完整视频"
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 700 }}
                                />
                              ) : null}
                              <Chip
                                label={display.text}
                                color={display.color}
                                size="small"
                                sx={{ fontWeight: 700, borderRadius: 1, px: 1 }}
                              />
                              {hasScore && confidencePct !== null ? (
                                <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    fontWeight={600}
                                  >
                                    置信度
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    color="secondary.main"
                                  >
                                    {confidencePct}%
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.disabled" italic>
                                  等待分析结果
                                </Typography>
                              )}
                              <Box sx={{ color: 'divider', ml: 1 }}>
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M9 18l6-6-6-6" />
                                </svg>
                              </Box>
                            </Stack>
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: alpha(productTokens.iris, 0.025),
          borderStyle: 'dashed',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          当前版本默认以数据采集和筛查参考为主，不输出诊断结论。
        </Typography>
      </Paper>
    </Container>
  );
}
